function _check_private_redeclaration(obj, privateCollection) {
    if (privateCollection.has(obj)) {
        throw new TypeError("Cannot initialize the same private elements twice on an object");
    }
}
function _class_apply_descriptor_get(receiver, descriptor) {
    if (descriptor.get) {
        return descriptor.get.call(receiver);
    }
    return descriptor.value;
}
function _class_apply_descriptor_set(receiver, descriptor, value) {
    if (descriptor.set) {
        descriptor.set.call(receiver, value);
    } else {
        if (!descriptor.writable) {
            throw new TypeError("attempted to set read only private field");
        }
        descriptor.value = value;
    }
}
function _class_extract_field_descriptor(receiver, privateMap, action) {
    if (!privateMap.has(receiver)) {
        throw new TypeError("attempted to " + action + " private field on non-instance");
    }
    return privateMap.get(receiver);
}
function _class_private_field_get(receiver, privateMap) {
    var descriptor = _class_extract_field_descriptor(receiver, privateMap, "get");
    return _class_apply_descriptor_get(receiver, descriptor);
}
function _class_private_field_init(obj, privateMap, value) {
    _check_private_redeclaration(obj, privateMap);
    privateMap.set(obj, value);
}
function _class_private_field_set(receiver, privateMap, value) {
    var descriptor = _class_extract_field_descriptor(receiver, privateMap, "set");
    _class_apply_descriptor_set(receiver, descriptor, value);
    return value;
}
import { ModelInstanceDocument } from '@ceramicnetwork/stream-model-instance';
import { StreamID, StreamRef } from '@ceramicnetwork/streamid';
import DataLoader from 'dataloader';
/** @internal */ export function idToString(id) {
    return typeof id === 'string' ? StreamRef.from(id).toString() : id.toString();
}
const tempBatchLoadFn = ()=>Promise.resolve([]);
var _ceramic = /*#__PURE__*/ new WeakMap(), _useCache = /*#__PURE__*/ new WeakMap();
export class DocumentLoader extends DataLoader {
    /**
   * Add a ModelInstanceDocument to the local cache, if enabled.
   */ cache(stream) {
        if (!_class_private_field_get(this, _useCache)) {
            return false;
        }
        const id = stream.id.toString();
        this.clear(id).prime(id, stream);
        return true;
    }
    /**
   * Create a new ModelInstanceDocument and add it to the cache, if enabled.
   */ async create(model, content, { controller, ...options } = {}) {
        const metadata = {
            controller,
            model: model instanceof StreamID ? model : StreamID.fromString(model)
        };
        const stream = await ModelInstanceDocument.create(_class_private_field_get(this, _ceramic), content, metadata, options);
        this.cache(stream);
        return stream;
    }
    /**
   * Load a ModelInstanceDocument from the cache (if enabled) or remotely.
   */ async load(id) {
        return await super.load(id);
    }
    /**
   * Create or load a deterministic ModelInstanceDocument and cache it.
   */ async single(controller, model, options) {
        const metadata = {
            controller,
            model: model instanceof StreamID ? model : StreamID.fromString(model)
        };
        const stream = await ModelInstanceDocument.single(_class_private_field_get(this, _ceramic), metadata, options);
        this.cache(stream);
        return stream;
    }
    /**
   * Update a ModelInstanceDocument after loading the stream remotely, bypassing the cache.
   */ async update(streamID, content, { replace, version, ...options } = {}) {
        const id = idToString(streamID);
        this.clear(id);
        const stream = await this.load(id);
        if (version != null && stream.commitId.toString() !== version) {
            throw new Error('Stream version mismatch');
        }
        const newContent = replace ? content : {
            ...stream.content,
            ...content
        };
        await stream.replace(newContent, options);
        return stream;
    }
    constructor(params){
        super(tempBatchLoadFn, {
            cache: true,
            cacheKeyFn: idToString,
            cacheMap: params.cache != null && typeof params.cache !== 'boolean' ? params.cache : undefined
        });
        _class_private_field_init(this, _ceramic, {
            writable: true,
            value: void 0
        });
        _class_private_field_init(this, _useCache, {
            writable: true,
            value: void 0
        });
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore internal method
        this._batchLoadFn = async (keys)=>{
            if (!params.cache) {
                // Disable cache but keep batching behavior - from https://github.com/graphql/dataloader#disabling-cache
                this.clearAll();
            }
            const results = await params.ceramic.multiQuery(keys.map(idToString).map((streamId)=>({
                    streamId
                })), params.multiqueryTimeout);
            return keys.map((key)=>{
                const id = idToString(key);
                const doc = results[id];
                return doc ? doc : new Error(`Failed to load document: ${id}`);
            });
        };
        _class_private_field_set(this, _ceramic, params.ceramic);
        _class_private_field_set(this, _useCache, !!params.cache);
    }
}
